import type { IUserResponse } from '~/types/server.types';
import { TGlobalContext } from '~/types/app.types';
import { getLegacyAppBaseUrl } from '~/utils/environment.utils';
import { RF_ROLLOUT_STORAGE_KEY } from '~/apps/main/App/hooks/useRFRollout/consts';
import { action } from 'mobx';

export interface ILogoutProps {
    forceCrossLogout?: boolean;
}

export const defaultLogoutProps = {
    forceCrossLogout: false
};

// TODO: move to a more appropriate place
export const REACT_STORAGE_AUTH_KEY = 'HONEYBOOK_REACT_CURR_USER';

// TODO: move to a more appropriate place
export function setUserInLocalStorage(user: IUserResponse) {
    localStorage.setItem(REACT_STORAGE_AUTH_KEY, JSON.stringify(user));
}

export abstract class BaseSessionCtrl {
    globalContext: TGlobalContext;
    appBasePath: string;

    protected constructor(globalContext: TGlobalContext, appBasePath: string) {
        this.globalContext = globalContext;
        this.appBasePath = appBasePath;
    }

    localLogin(user: IUserResponse, update = false) {
        if (update) {
            this.updateUserInLocalStorage(user);
        } else {
            this.setUserInLocalStorage(user);
        }
        this.setUserInSession(user);
    }

    setUserInLocalStorage(user: IUserResponse) {
        this.globalContext.storageService.setItem(
            REACT_STORAGE_AUTH_KEY,
            JSON.stringify(user)
        );
    }

    getUserFromLocalStorage() {
        return JSON.parse(
            this.globalContext.storageService.getItem(REACT_STORAGE_AUTH_KEY)
        ) as IUserResponse;
    }

    // updates the local storage with the new user data, without overwriting
    // existing data with data which is undefined
    updateUserInLocalStorage(user: IUserResponse) {
        const currUser = this.getUserFromLocalStorage();

        if (!currUser) {
            this.localLogin(user);
        }

        // Update only non-undefined fields from the new user object
        const updatedUser = {
            ...currUser,
            ...Object.fromEntries(
                Object.entries(user).filter(([, value]) => value !== undefined)
            )
        };

        this.setUserInLocalStorage(updatedUser);
    }

    loginFromLocalStorage() {
        const user = this.getUserFromLocalStorage();

        if (!user) {
            return;
        }

        this.setUserInSession(user);
    }

    logout(localLogoutProps: ILogoutProps = defaultLogoutProps) {
        const { userStore, sessionService } = this.globalContext;
        const user = userStore.currUser;

        sessionService.logout({ email: user?.email });
        this.localLogout(localLogoutProps);
    }

    @action
    localLogout({
        forceCrossLogout = defaultLogoutProps.forceCrossLogout
    }: ILogoutProps = defaultLogoutProps) {
        const {
            jStorageService,
            sessionStore,
            userStore,
            accountStore,
            companyStore
        } = this.globalContext;

        this.globalContext.storageService.removeItem(REACT_STORAGE_AUTH_KEY);
        localStorage.removeItem(RF_ROLLOUT_STORAGE_KEY);

        if (forceCrossLogout) {
            jStorageService.clear();
        }

        sessionStore.init();
        userStore.init();
        accountStore.init();
        companyStore.init();
    }

    logoutReactFirst() {
        this._postLogoutMessageToIframes();
        this.logout({ forceCrossLogout: true });
    }

    logoutByRedirect(origin: string) {
        const url =
            this.appBasePath + '/logout?origin=' + encodeURIComponent(origin);
        this.globalContext.storageService.setItem('redirectSource', 'react');
        window.location.href = url;
    }

    setUserInSession(user: IUserResponse) {
        const { userStore, sessionStore, companyStore } = this.globalContext;

        userStore.update(user);
        sessionStore.setCurrUserId(user._id);

        if (user.company) {
            companyStore.update(user.company);
            sessionStore.setCurrCompanyId(user.company._id);
        }
    }

    private _postLogoutMessageToIframes() {
        const legacyUrl = getLegacyAppBaseUrl();
        const iframes = document.querySelectorAll('iframe');
        iframes.forEach(iframe => {
            iframe.contentWindow?.postMessage(
                { status: 'logout', forceCrossLogout: true },
                legacyUrl
            );
        });
    }
}
